import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import {
	obscure,
	restore,
	shouldBlockShortcut,
	shouldObscureOnKeyDown,
	isPrintScreenKey,
} from '../../../shared/MediaProtection';

const RESTORE_DELAY_MS = 500;
const CAPTURE = true;

const MediaProtectionGuard = ({ active }) => {
	const restoreTimeoutRef = useRef(null);

	useEffect(() => {
		if (!active) {
			restore();
			return undefined;
		}

		const clearRestoreTimeout = () => {
			if (restoreTimeoutRef.current) {
				clearTimeout(restoreTimeoutRef.current);
				restoreTimeoutRef.current = null;
			}
		};

		const scheduleRestore = () => {
			clearRestoreTimeout();
			restoreTimeoutRef.current = setTimeout(restore, RESTORE_DELAY_MS);
		};

		const handleBlur = () => {
			obscure();
		};

		const handleFocus = () => {
			if (!document.hidden) {
				clearRestoreTimeout();
				restore();
			}
		};

		const handleVisibilityChange = () => {
			if (document.hidden) {
				obscure();
			} else {
				clearRestoreTimeout();
				restore();
			}
		};

		const handleKeyDown = (event) => {
			if (shouldObscureOnKeyDown(event)) {
				obscure();
				event.preventDefault();
				event.stopPropagation();
			}

			if (shouldBlockShortcut(event)) {
				event.preventDefault();
			}
		};

		const handleKeyUp = (event) => {
			if (isPrintScreenKey(event)) {
				scheduleRestore();
			}
		};

		window.addEventListener('blur', handleBlur);
		window.addEventListener('focus', handleFocus);
		document.addEventListener('visibilitychange', handleVisibilityChange);
		window.addEventListener('keydown', handleKeyDown, CAPTURE);
		window.addEventListener('keyup', handleKeyUp, CAPTURE);
		document.addEventListener('keydown', handleKeyDown, CAPTURE);
		document.addEventListener('keyup', handleKeyUp, CAPTURE);

		return () => {
			clearRestoreTimeout();
			window.removeEventListener('blur', handleBlur);
			window.removeEventListener('focus', handleFocus);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			window.removeEventListener('keydown', handleKeyDown, CAPTURE);
			window.removeEventListener('keyup', handleKeyUp, CAPTURE);
			document.removeEventListener('keydown', handleKeyDown, CAPTURE);
			document.removeEventListener('keyup', handleKeyUp, CAPTURE);
			restore();
		};
	}, [active]);

	return null;
};

MediaProtectionGuard.propTypes = {
	active: PropTypes.bool,
};

MediaProtectionGuard.defaultProps = {
	active: false,
};

export default MediaProtectionGuard;
